/**
 * Template list and CRUD management (E19-5: MED-501)
 * Updated for dynamic tier-based escalation (MED-543)
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Stack,
  Group,
  Button,
  Badge,
  ActionIcon,
  Text,
  Modal,
  TextInput,
  Select,
  Switch,
  Loader,
  Center,
  Paper,
  Code,
  Flex,
  Box,
  Divider,
} from '@mantine/core';
import { RichTextEditor } from '../../../../components/common/RichTextEditor/RichTextEditor';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import {
  absenceEmailTemplatesApi,
  type AbsenceEmailTemplateWithRuleCount,
  type CreateTemplateInput,
  type EscalationTier,
} from '../../../../services/api/absenceEmailManagement';

const AVAILABLE_VARIABLES = [
  '{{participant_name}}',
  '{{session_number}}',
  '{{program_name}}',
  '{{cohort_name}}',
  '{{coach_name}}',
  '{{next_session_date}}',
  '{{next_session_time}}',
  '{{group_name}}',
  '{{absence_count}}',
];

const ESCALATION_TIER_OPTIONS = [
  { value: 'first_warning', label: 'First Warning' },
  { value: 'first_warning_with_offer', label: 'First Warning + Offer' },
  { value: 'mid_escalation', label: 'Mid-Escalation' },
  { value: 'deferral_offer', label: 'Deferral Offer' },
  { value: 'forced_deferral', label: 'Forced Deferral' },
];

const TIER_COLORS: Record<string, string> = {
  first_warning: 'blue',
  first_warning_with_offer: 'cyan',
  mid_escalation: 'warningAmber',
  deferral_offer: 'orange',
  forced_deferral: 'red',
};

interface TemplateFormData {
  name: string;
  subject_template: string;
  body_template: string;
  escalation_tier: EscalationTier;
  is_active: boolean;
}

const defaultFormData: TemplateFormData = {
  name: '',
  subject_template: '',
  body_template: '',
  escalation_tier: 'first_warning',
  is_active: true,
};

export function AbsenceTemplateList() {
  const [templates, setTemplates] = useState<AbsenceEmailTemplateWithRuleCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false);
  const [editingTemplate, setEditingTemplate] =
    useState<AbsenceEmailTemplateWithRuleCount | null>(null);
  const [deletingTemplate, setDeletingTemplate] =
    useState<AbsenceEmailTemplateWithRuleCount | null>(null);
  const [formData, setFormData] = useState<TemplateFormData>(defaultFormData);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await absenceEmailTemplatesApi.list();
      setTemplates(data || []);
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to load templates', color: 'red' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleOpenCreate = () => {
    setEditingTemplate(null);
    setFormData(defaultFormData);
    openModal();
  };

  const handleOpenEdit = (template: AbsenceEmailTemplateWithRuleCount) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      subject_template: template.subject_template,
      body_template: template.body_template,
      escalation_tier: template.escalation_tier,
      is_active: template.is_active,
    });
    openModal();
  };

  const handleOpenDelete = (template: AbsenceEmailTemplateWithRuleCount) => {
    setDeletingTemplate(template);
    openDeleteModal();
  };

  const handleSave = async () => {
    if (!formData.name || !formData.subject_template || !formData.body_template) {
      notifications.show({
        title: 'Validation Error',
        message: 'Name, subject, and body are required',
        color: 'red',
      });
      return;
    }

    setSaving(true);
    try {
      const payload: CreateTemplateInput = {
        name: formData.name,
        subject_template: formData.subject_template,
        body_template: formData.body_template,
        escalation_tier: formData.escalation_tier,
        is_active: formData.is_active,
      };

      if (editingTemplate) {
        await absenceEmailTemplatesApi.update(editingTemplate.id, payload);
        notifications.show({
          title: 'Success',
          message: 'Template updated successfully',
          color: 'green',
        });
      } else {
        await absenceEmailTemplatesApi.create(payload);
        notifications.show({
          title: 'Success',
          message: 'Template created successfully',
          color: 'green',
        });
      }
      closeModal();
      fetchTemplates();
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to save template';
      notifications.show({ title: 'Error', message, color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingTemplate) return;
    try {
      await absenceEmailTemplatesApi.delete(deletingTemplate.id);
      notifications.show({
        title: 'Success',
        message: 'Template deleted successfully',
        color: 'green',
      });
      closeDeleteModal();
      fetchTemplates();
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to delete template';
      notifications.show({ title: 'Error', message, color: 'red' });
    }
  };

  const handleToggleActive = async (template: AbsenceEmailTemplateWithRuleCount) => {
    try {
      await absenceEmailTemplatesApi.update(template.id, {
        is_active: !template.is_active,
      });
      notifications.show({
        title: 'Success',
        message: `Template ${template.is_active ? 'deactivated' : 'activated'}`,
        color: 'green',
      });
      fetchTemplates();
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to update template';
      notifications.show({ title: 'Error', message, color: 'red' });
    }
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  const tierLabel = (tier: string) =>
    ESCALATION_TIER_OPTIONS.find((o) => o.value === tier)?.label ?? tier;

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={600} fz={18}>
          Absence Email Templates
        </Text>
        <Button
          leftSection={<Plus size={14} />}
          size="xs"
          color="medleyGreen"
          onClick={handleOpenCreate}
        >
          Create New Template
        </Button>
      </Group>

      <Paper withBorder radius="md" styles={{ root: { boxShadow: 'none' } }}>
        {/* Column headers */}
        <Group
          gap="xl"
          px="lg"
          py="sm"
          style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}
        >
          <Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ flex: 1, letterSpacing: '0.5px' }}>
            Template Name
          </Text>
          <Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ minWidth: 120, letterSpacing: '0.5px' }}>
            Tier
          </Text>
          <Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ minWidth: 80, letterSpacing: '0.5px' }}>
            Status
          </Text>
          <Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ minWidth: 80, letterSpacing: '0.5px' }}>
            Rules Using
          </Text>
          <Box style={{ minWidth: 70 }}>
            <Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.5px' }}>
              Actions
            </Text>
          </Box>
        </Group>
        <Divider />

        {/* Rows */}
        <Stack gap={0}>
          {templates.length === 0 ? (
            <Center py="lg">
              <Text c="dimmed" size="sm">
                No templates yet. Create one to get started.
              </Text>
            </Center>
          ) : (
            templates.map((template) => {
              const isHovered = hoveredId === template.id;
              return (
                <Box
                  key={template.id}
                  style={{
                    borderLeft: isHovered ? '3px solid #28463A' : '3px solid transparent',
                    backgroundColor: isHovered ? '#F5F7F6' : 'transparent',
                    cursor: 'default',
                    transition: 'background-color 0.15s ease',
                  }}
                  onMouseEnter={() => setHoveredId(template.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  <Group
                    gap="xl"
                    py="sm"
                    px="lg"
                    style={{
                      paddingLeft: isHovered ? 'calc(var(--mantine-spacing-lg) - 3px)' : 'var(--mantine-spacing-lg)',
                    }}
                  >
                    {/* Template Name */}
                    <Text size="sm" fw={500} style={{ flex: 1, color: '#28463A' }}>
                      {template.name}
                    </Text>

                    {/* Tier */}
                    <Box style={{ minWidth: 120 }}>
                      <Badge
                        size="xs"
                        variant="light"
                        color={TIER_COLORS[template.escalation_tier] ?? 'gray'}
                        radius={3}
                      >
                        {tierLabel(template.escalation_tier)}
                      </Badge>
                    </Box>

                    {/* Status */}
                    <Box style={{ minWidth: 80 }}>
                      <Badge
                        size="xs"
                        variant="filled"
                        color={template.is_active ? 'medleyGreen' : 'gray'}
                        radius={3}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleToggleActive(template)}
                      >
                        {template.is_active ? 'ACTIVE' : 'INACTIVE'}
                      </Badge>
                    </Box>

                    {/* Rules Using */}
                    <Text size="xs" c="dimmed" style={{ minWidth: 80 }}>
                      {template.rule_count > 0
                        ? `${template.rule_count} rule${template.rule_count !== 1 ? 's' : ''}`
                        : '—'}
                    </Text>

                    {/* Actions */}
                    <Group gap={4} style={{ minWidth: 70 }}>
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        onClick={() => handleOpenEdit(template)}
                      >
                        <Pencil size={14} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        color="red"
                        onClick={() => handleOpenDelete(template)}
                        disabled={template.rule_count > 0}
                      >
                        <Trash2 size={14} />
                      </ActionIcon>
                    </Group>
                  </Group>
                </Box>
              );
            })
          )}
        </Stack>
      </Paper>

      {/* Create/Edit Modal */}
      <Modal
        opened={modalOpened}
        onClose={closeModal}
        title={editingTemplate ? 'Edit Template' : 'Create Template'}
        size="lg"
      >
        <Stack gap="md">
          <TextInput
            label="Template Name"
            placeholder="e.g., First Warning — Unexcused"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.currentTarget.value })}
          />

          <Select
            label="Escalation Tier"
            data={ESCALATION_TIER_OPTIONS}
            value={formData.escalation_tier}
            onChange={(val) =>
              setFormData({ ...formData, escalation_tier: (val as EscalationTier) || 'first_warning' })
            }
            required
          />

          <Switch
            label="Active"
            description="Only active templates can be used by the rule engine"
            checked={formData.is_active}
            onChange={(e) => setFormData({ ...formData, is_active: e.currentTarget.checked })}
          />

          <TextInput
            label="Subject Line"
            placeholder="e.g., Your {{program_name}} session this week"
            required
            value={formData.subject_template}
            onChange={(e) =>
              setFormData({ ...formData, subject_template: e.currentTarget.value })
            }
          />

          <RichTextEditor
            label="Email Body *"
            value={formData.body_template}
            onChange={(html) => setFormData({ ...formData, body_template: html })}
            minHeight={200}
          />

          <div>
            <Text fz={12} fw={600} mb={4}>
              Available Variables
            </Text>
            <Flex gap={4} wrap="wrap">
              {AVAILABLE_VARIABLES.map((v) => (
                <Code key={v} fz={11}>
                  {v}
                </Code>
              ))}
            </Flex>
          </div>

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={closeModal}>
              Cancel
            </Button>
            <Button color="medleyGreen" onClick={handleSave} loading={saving}>
              {editingTemplate ? 'Save Changes' : 'Create Template'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        title="Delete Template"
        size="sm"
      >
        <Stack gap="md">
          <Text fz="sm">
            Are you sure you want to delete &ldquo;{deletingTemplate?.name}&rdquo;? This action
            cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={closeDeleteModal}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDelete}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
